import { useState, useMemo, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import KBHero from './components/KBHero';
import KBSidebar from './components/KBSidebar';
import KBArticleCard from './components/KBArticleCard';
import KBArticleDetail from './components/KBArticleDetail';
import { kbArticles, kbCategories } from '../../mocks/knowledge-base';

const categoryColors: Record<string, string> = {
  specs: 'bg-primary/10 text-primary',
  setup: 'bg-green-100 text-green-700',
  troubleshooting: 'bg-orange-100 text-orange-700',
  'best-practices': 'bg-secondary/10 text-secondary',
};

export default function KnowledgeBasePage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  const counts = useMemo(() => {
    const result: Record<string, number> = { all: kbArticles.length };
    kbCategories.forEach((cat) => {
      if (cat.id !== 'all') {
        result[cat.id] = kbArticles.filter((a) => a.category === cat.id).length;
      }
    });
    return result;
  }, []);

  const filteredArticles = useMemo(() => {
    return kbArticles.filter((article) => {
      const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        article.title.toLowerCase().includes(q) ||
        article.summary.toLowerCase().includes(q) ||
        article.tags.some((t) => t.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const getCategoryLabel = (catId: string) =>
    kbCategories.find((c) => c.id === catId)?.label ?? catId;

  const handleArticleClick = (id: string) => {
    setSelectedArticleId(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setSelectedArticleId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    document.title = 'Base de Conhecimento | STIB Palestra Silenciosa | Guias Técnicos';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Guias técnicos, especificações de equipamentos e melhores práticas para palestra silenciosa e conferência silenciosa. Documentação completa para organizadores de eventos.');
    }

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    const collectionSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Base de Conhecimento - STIB Palestra Silenciosa',
      description: 'Guias técnicos, especificações de equipamentos e melhores práticas para palestra silenciosa e conferência silenciosa.',
      url: `${siteUrl}/knowledge-base`,
      inLanguage: 'pt-BR',
      provider: {
        '@type': 'Organization',
        name: 'STIB - Palestra Silenciosa São Paulo',
        url: siteUrl
      }
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Base de Conhecimento', item: `${siteUrl}/knowledge-base` }
      ]
    };

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.id = 'kb-collection-schema';
    s1.text = JSON.stringify(collectionSchema);
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    s2.type = 'application/ld+json';
    s2.id = 'kb-breadcrumb-schema';
    s2.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(s2);

    return () => {
      ['kb-collection-schema', 'kb-breadcrumb-schema'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      <Header />

      <KBHero
        searchQuery={searchQuery}
        onSearchChange={(q) => {
          setSearchQuery(q);
          setSelectedArticleId(null);
        }}
        totalArticles={kbArticles.length}
      />

      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-8">
          {[
            { icon: 'ri-file-text-line', label: 'Technical Specs', value: counts['specs'] ?? 0 },
            { icon: 'ri-tools-line', label: 'Setup Guides', value: counts['setup'] ?? 0 },
            { icon: 'ri-bug-line', label: 'Troubleshooting', value: counts['troubleshooting'] ?? 0 },
            { icon: 'ri-star-line', label: 'Best Practices', value: counts['best-practices'] ?? 0 },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center text-primary">
                <i className={`${stat.icon} text-base`} aria-hidden="true"></i>
              </div>
              <span className="font-heading font-bold text-secondary text-sm">{stat.value}</span>
              <span className="font-body text-gray-400 text-sm">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-8 items-start">
          {/* Sidebar */}
          <KBSidebar
            activeCategory={activeCategory}
            onCategoryChange={(id) => {
              setActiveCategory(id);
              setSelectedArticleId(null);
            }}
            counts={counts}
          />

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            {selectedArticleId ? (
              <KBArticleDetail articleId={selectedArticleId} onBack={handleBack} />
            ) : (
              <>
                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading font-bold text-xl text-secondary">
                      {activeCategory === 'all' ? 'All Articles' : getCategoryLabel(activeCategory)}
                    </h2>
                    <p className="font-body text-sm text-gray-400 mt-0.5">
                      {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} found
                      {searchQuery && (
                        <span> for &ldquo;<strong className="text-secondary">{searchQuery}</strong>&rdquo;</span>
                      )}
                    </p>
                  </div>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-sm text-primary font-body hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Clear search
                    </button>
                  )}
                </div>

                {filteredArticles.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                      <i className="ri-search-line text-2xl text-gray-400" aria-hidden="true"></i>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-secondary mb-2">No articles found</h3>
                    <p className="font-body text-sm text-gray-400 mb-6">
                      Try a different search term or browse all categories.
                    </p>
                    <button
                      onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                      className="bg-primary text-white px-6 py-2.5 rounded-full font-body font-semibold text-sm hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Browse All Articles
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {filteredArticles.map((article) => (
                      <KBArticleCard
                        key={article.id}
                        article={article}
                        onClick={handleArticleClick}
                        categoryLabel={getCategoryLabel(article.category)}
                        categoryColor={categoryColors[article.category] ?? 'bg-gray-100 text-gray-600'}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Bottom CTA */}
      {!selectedArticleId && (
        <section className="bg-gradient-to-r from-secondary to-primary/80 py-16 mt-8">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-heading font-bold text-3xl text-white mb-4">
              Need a Custom Technical Consultation?
            </h2>
            <p className="font-body text-white/80 text-base mb-8 max-w-xl mx-auto leading-relaxed">
              Our engineers can assess your venue, event size, and language requirements to design the perfect silent conference setup.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contato"
                className="inline-flex items-center justify-center gap-2 bg-white text-secondary px-7 py-3.5 rounded-full font-heading font-semibold text-sm hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-message-3-line" aria-hidden="true"></i>
                Request Consultation
              </a>
              <a
                href="tel:+5511917776227"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-7 py-3.5 rounded-full font-heading font-semibold text-sm hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line" aria-hidden="true"></i>
                (11) 91777-6227
              </a>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
